import React, { useEffect, useRef, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import axios from '../../admin-axios';
import useActions from '../../hooks/useActions';
import ApprovedReceipt from '../../printer/ApprovedReceipt';
import { detectError, numberWithCommas } from '../../utils';
import CINBox from '../CINBox';
import { PromptModal, SuccessModal } from '../Modals';
import classes from './index.module.scss';

const ApprovedTransactionView = () => {
  const { txId } = useParams();

  const {
    user: { stationId, id }
  } = useSelector((state) => state.auth);
  const [loading, setLoading] = useState(false);
  const [transaction, setTransaction] = useState(null);
  const [cylinders, setCylinders] = useState(null);
  const [payment, setPayment] = useState(null);
  const [currency, setCurrency] = useState('');
  const [reload, setReload] = useState(false);
  const [debt, setDebt] = useState('');
  const [totalAmountPaid, setTotalAmountPaid] = useState(0);
  const [printData, setPrintData] = useState(null);
  const [loadingPrint, setLoadingPrint] = useState(false);
  const [showPartDebt, setPartDebtShow] = useState(false);
  const [showPartDebtSuccess, setPartDebtSuccessShow] = useState(false);
  const [showClearDebtSuccess, setClearDebtSuccessShow] = useState(false);
  const [partDebtLoading, setPartDebtLoading] = useState(false);
  const [showClearDebt, setClearDebtShow] = useState(false);
  const [clearDebtLoading, setClearDebtLoading] = useState(false);
  const handlePartDebtClose = () => setPartDebtShow(false);
  const handlePartDebtShow = () => setPartDebtShow(true);
  const handleClearDebtClose = () => setClearDebtShow(false);
  const handleClearDebtShow = () => setClearDebtShow(true);

  const debtInputRef = useRef(null);

  const { setSubHeader, getApprovedTransactions } = useActions();

  useEffect(() => {
    return () => {
      setSubHeader('');
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const fetchTransaction = async () => {
      try {
        setLoading(true);

        const response = await axios.get(`/transaction/approved/details/${txId}`);

        setSubHeader(response.data.data.customer_type);

        setTransaction(response.data);
        setCylinders(response.data.cylinder);
        setPayment(response.data.payment);
        setCurrency(response.data.currency);
        setDebt(response.data.total_debt);
        setTotalAmountPaid(response.data.total_amount_paid);
        setLoading(false);
      } catch (e) {
        setLoading(false);
      }
    };

    fetchTransaction();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [txId, reload]);

  useEffect(() => {
    const fetchPrintData = async () => {
      try {
        setLoadingPrint(true);

        const response = await axios.get(`/transaction/receipt/print/${txId}`);

        setPrintData(response.data);
        setLoadingPrint(false);
      } catch (e) {
        setLoadingPrint(false);
        toast.error('Print data could not be loaded!');
      }
    };

    fetchPrintData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [txId, reload]);

  useEffect(() => {
    document.body.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const showDebtHandler = () => {
    if (!debtInputRef.current.value) {
      toast.error('Please enter payment amount');
      return;
    }

    const totalDebt = Math.abs(parseFloat(debt));
    const amountPaid = parseFloat(debtInputRef.current.value);

    if (amountPaid < totalDebt) {
      handlePartDebtShow();
    } else {
      handleClearDebtShow();
    }
  };

  const payDebtHandler = async () => {
    try {
      const totalDebt = Math.abs(parseFloat(debt));
      const amountPaid = parseFloat(debtInputRef.current.value);

      if (amountPaid < totalDebt) {
        setPartDebtLoading(true);
      } else {
        setClearDebtLoading(true);
      }

      await axios.post(`debt/repayment`, {
        tranx_id: txId,
        station_id: stationId,
        amount: debtInputRef.current.value,
        user_id: id
      });

      setReload((prevState) => !prevState);
      if (amountPaid < totalDebt) {
        handlePartDebtClose();
        setPartDebtLoading(false);
        setPartDebtSuccessShow(true);
      } else {
        getApprovedTransactions(stationId, 1);
        handleClearDebtClose();
        setClearDebtLoading(false);
        setClearDebtSuccessShow(true);
      }
    } catch (e) {
      const totalDebt = Math.abs(parseFloat(debt));
      const amountPaid = parseFloat(debtInputRef.current.value);

      if (amountPaid < totalDebt) {
        setPartDebtLoading(false);
      } else {
        setClearDebtLoading(false);
      }
      toast.error(detectError(e));
    }
  };

  return (
    <>
      <div>
        {loading ? (
          <div className="d-flex justify-content-center w-100">
            <Spinner className="spinner" animation="border" variant="dark" />
          </div>
        ) : (
          <div className="row g-5">
            <div className="col-xl-6">
              <div className={classes.view_transaction}>
                <div className="row g-3 mb-4">
                  <div className="col-sm-8">
                    <div className={`${classes.view_transaction_id} p-2`}>
                      <div className="d-flex align-items-center">
                        <span>Transaction ID</span>
                        <span>{txId}</span>
                      </div>
                    </div>
                  </div>
                  <div className="col-sm-4">
                    <div
                      className={`w-100 h-100 ${classes.view_transaction_date} p-2 d-flex align-items-center`}>
                      {transaction && new Date(transaction.data.date_created).toDateString()}
                    </div>
                  </div>
                </div>
                <div className={`${classes.view_transaction_box} w-100 p-4 mb-4`}>
                  <h5>Cylinder Identification Numbers</h5>
                  <CINBox cins={cylinders || []} total_quantity={transaction?.data?.total_gas} />
                </div>
                <div className="row">
                  <div className="col-5">
                    <div>
                      <div className="fw-bold mb-2">Price/Kg</div>
                      <div className={classes.view_transaction_pricekg}>
                        {currency} {transaction && transaction.data.gas_price}
                      </div>
                    </div>
                  </div>
                  <div className="col-7">
                    <div>
                      <div className="fw-bold mb-2">Amount</div>
                      <div className={classes.view_transaction_amount}>
                        {currency} {transaction && numberWithCommas(transaction.data.amount, 2)}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-xl-6">
              <div className="view_payment">
                <div className="row">
                  <div className="col-8">
                    <h2 className="py-2 mb-3">
                      {transaction && `${transaction.data.lastname} ${transaction.data.firstname}`}
                    </h2>
                  </div>
                  <div className="col-4 py-2">
                    {transaction && transaction.data.status === 'Cleared' ? (
                      <div
                        className="p-2 text-center fw-bold"
                        style={{
                          border: '1px solid #08D4A4',
                          borderRadius: '3px',
                          color: '#08D4A4'
                        }}>
                        Cleared
                      </div>
                    ) : (
                      <div
                        className="p-2 text-center fw-bold"
                        style={{
                          border: '1px solid #EB5757',
                          borderRadius: '3px',
                          color: '#EB5757',
                          width: '100%'
                        }}>
                        Uncleared
                      </div>
                    )}
                  </div>
                </div>
                {payment &&
                  payment.map((value, i) => (
                    <div key={i} className="row mb-4">
                      <div className="col-12 col-sm-5">
                        <div>
                          <div className={`fw-bold mb-2 ${classes.view_payment_title}`}>
                            Payment Method
                          </div>
                          <div className={classes.view_payment_values}>{value.method}</div>
                        </div>
                      </div>
                      <div className="col-12 col-sm-7">
                        <div>
                          <div className={`fw-bold mb-2 ${classes.view_payment_title}`}>
                            Amount Paid
                          </div>
                          <div className={classes.view_payment_values}>
                            {currency} {numberWithCommas(value.amount, 2)}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}

                <div className="row mb-4">
                  <div className="col-12">
                    <div>
                      <div className={`fw-bold mb-2 ${classes.view_payment_title}`}>
                        Total Amount Paid
                      </div>
                      <div className={classes.view_payment_values}>
                        {currency} {numberWithCommas(totalAmountPaid, 2)}
                      </div>
                    </div>
                  </div>
                </div>
                {transaction &&
                  transaction.debt_repayment.length > 0 &&
                  transaction.debt_repayment.map((value, i) => (
                    <div key={i} className="row mb-4">
                      <div className="col-12 col-sm-5">
                        {/* <div> */}
                        {/*  <div className={`fw-bold mb-2 ${classes.view_payment_title}`}>Debt</div> */}
                        {/*  <div className={classes.view_payment_values}> */}
                        {/*    {currency} {transaction.total_debt} */}
                        {/*  </div> */}
                        {/* </div> */}
                      </div>
                      <div className="col-12 col-sm-7">
                        <div>
                          <div className={`fw-bold mb-2 ${classes.view_payment_title}`}>
                            Debt Repayment Date: {new Date(value.created_at).toDateString()}
                          </div>
                          <div className={classes.view_payment_values}>
                            {currency} {numberWithCommas(value.amount, 2)}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                {debt !== '' && Math.abs(+debt) > 0 && (
                  <div className="row">
                    <div className="col-12 col-sm-5">
                      <div>
                        <div className={`fw-bold mb-2 ${classes.view_payment_title}`}>Debt</div>
                        <div className={classes.view_payment_values}>
                          {currency} {numberWithCommas(Math.abs(+debt), 2)}
                        </div>
                      </div>
                    </div>
                    <div className="col-12 col-sm-7">
                      <div>
                        <div className={`fw-bold mb-2 ${classes.view_payment_title}`}>
                          Debt Repayment
                        </div>
                        <div className="">
                          <div className="input-group">
                            <span className="input-group-text">{currency}</span>
                            <input
                              type="text"
                              className={`${classes.debt_input} form-control`}
                              aria-label="Amount"
                              ref={debtInputRef}
                            />
                            <button
                              className={`btn ${classes.debt_button} px-4`}
                              type="button"
                              onClick={showDebtHandler}
                              id="button-addon2">
                              Pay
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <div className="row mt-5">
                <div className="col-12">
                  <button
                    onClick={() => window.print()}
                    type="button"
                    disabled={loadingPrint || printData === null}
                    className={`btn p-2 w-100 fw-bold fs-6 ${classes.print_button}`}>
                    Print Receipt
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      {printData && <ApprovedReceipt printData={printData} />}
      <div className="approved_modals">
        <PromptModal
          title="Pay Part Debt"
          message="Are you sure you want to pay part of the debt"
          showPrompt={showPartDebt}
          handlePromptClose={handlePartDebtClose}
          promptActionHandler={payDebtHandler}
          loading={partDebtLoading}
        />
        <SuccessModal
          message="You've successfully cleared part of the debt!"
          showSuccess={showPartDebtSuccess}
          setShowSuccess={setPartDebtSuccessShow}
        />
        <PromptModal
          title="Clear All Debt"
          message="Are you sure you want to pay all the debt"
          showPrompt={showClearDebt}
          handlePromptClose={handleClearDebtClose}
          promptActionHandler={payDebtHandler}
          loading={clearDebtLoading}
        />
        <SuccessModal
          message="You've successfully cleared all the debt!"
          showSuccess={showClearDebtSuccess}
          setShowSuccess={setClearDebtSuccessShow}
        />
      </div>
    </>
  );
};

export default ApprovedTransactionView;
